import React, { useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../../../models/DTO/IErrorDTO";
import SupplierContext from "../../../../../contexts/SupplierContext";
import UserContext from "../../../../../contexts/UserContext";
import OwTitle from "../../../../shared/OwTitle/OwTitle";
import OwButton from "../../../../shared/OwButton/OwButton";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../../../shared/OwFnInput/OwFnInput";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import QbdCustomFieldsTypeEnum from "../../../../../models/QbdCustomFieldsTypeEnum";
import IQBDProcessCustomFieldDTO from "../../../../../models/DTO/IQBDProcessCustomFieldDTO";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IQBDOnProcessCustomFieldDTO from "../../../../../models/DTO/IQBDOnProcessCustomFieldDTO";
import INotification from "../../../../../models/INotification";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import IQbd_Custom_Field from "../../../../../models/IQbd_Custom_Field";
import { QbdCustomFieldRow } from "./QbdCustomFieldRow/QbdCustomFieldRow";
import IQBDOnGetCustomFieldsDTO from "../../../../../models/DTO/IQBDOnGetCustomFieldsDTO";
import IQBDGetCustomFieldsDTO from "../../../../../models/DTO/IQBDGetCustomFieldsDTO";
import OwCheckbox from "../../../../shared/OwCheckbox/OwCheckbox";
import UiContext from "@contexts/UiContext";
import { SocketContext } from "@contexts/SocketContext";

type Props = {
    closeSidebar: () => void;
};

export const QbdCustomFieldsSidebar = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [cfName, setCfName] = useState("");
    const [cfType, setCfType] = useState<QbdCustomFieldsTypeEnum>();
    const [cfDestProp, setCfDestProp] = useState("");
    const [cfDisplayOnly, setCfDisplayOnly] = useState(false);
    const [saveActive, setSaveActive] = useState(false);
    const [existingCustomFields, setExistingCustomFields] = useState(supplierModel.Supplier_Integration.QBD_Custom_Fields || []);

    useEffect(() => {
        socket.on(EndpointsEnum.QBDOnProcessCustomField, onProcessCustomField);
        socket.on(EndpointsEnum.QBDOnGetCustomFields, onGetCustomFields);

        getCustomFields();

        return () => {
            socket.removeListener(EndpointsEnum.QBDOnProcessCustomField, onProcessCustomField);
            socket.removeListener(EndpointsEnum.QBDOnGetCustomFields, onGetCustomFields);
        };
    }, []);

    const cfTypeOptions: { value: QbdCustomFieldsTypeEnum; title: string }[] = [
        { value: QbdCustomFieldsTypeEnum.ITEMS, title: "Items" },
        { value: QbdCustomFieldsTypeEnum.CUSTOMERS, title: "Customers" },
    ];

    const cfItemDestPropOptions: { value: string; title: string }[] = [
        { value: "name", title: "Name" },
        { value: "sku", title: "SKU" },
        { value: "description", title: "Description" },
        { value: "item_weight", title: "Item Weight" },
        { value: "upc", title: "UPC" },
    ];

    const cfCustomerDestOptions: { value: string; title: string }[] = [
        { value: "name", title: "Name" },
        { value: "company_name", title: "Company Name" },
    ];

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case "cfName":
                setCfName(value);
                break;

            case "cfType":
                setCfType(value);
                break;

            case "cfDestProp":
                setCfDestProp(value);
                break;

            case "cfDisplayOnly":
                setCfDisplayOnly(value);
                break;
        }
    };

    const saveFn = () => {
        if (cfName.length && cfType && (cfDestProp.length || cfDisplayOnly)) {
            const payload: IQBDProcessCustomFieldDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                customField: {
                    name: cfName,
                    type: cfType,
                    dest_prop: cfDisplayOnly ? "" : cfDestProp,
                    supplier_id: supplierModel.id,
                    display_only: cfDisplayOnly,
                },
            };

            socket.emit(EndpointsEnum.QBDProcessCustomField, payload);
        }
    };

    const updateFn = (existingCf: IQbd_Custom_Field) => {};

    const deleteFn = (existingCf: IQbd_Custom_Field) => {};

    const getCustomFields = () => {
        const payload: IQBDGetCustomFieldsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBDGetCustomFields, payload);
    };

    const onGetCustomFields = (data: IQBDOnGetCustomFieldsDTO) => {
        setExistingCustomFields(data.customFields);
    };

    const onProcessCustomField = (data: IQBDOnProcessCustomFieldDTO) => {
        const notification: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_created_custom_field",
        };

        if (data.isUpdated) {
            notification.message = "successfully_updated_custom_field";
        }

        if (data.isDeleted) {
            notification.message = "successfully_deleted_custom_field";
        }

        uiContext.notifyFn(null, notification);

        getCustomFields();

        if (data.isCreated) {
            setCfName("");
            setCfType(undefined);
            setCfDestProp("");
            setCfDisplayOnly(false);
        }
    };

    return (
        <div className={"QbdCustomFieldsSidebar p-2"}>
            <div className={"d-flex align-items-center justify-content-between"}>
                <div>
                    <OwTitle title={"edit_custom_fields"} tag={"h4"} />
                </div>
                <div>
                    <OwButton className={"bg-danger"} centerIconClass={"fas fa-times"} fn={props.closeSidebar} />
                </div>
            </div>

            <div className="mt-4 d-flex flex-column">
                <div>
                    <OwTitle title={"new_custom_field"} tag={"h5"} />
                    <div>
                        <div>
                            <OwLabel title={"name"} />
                            <OwFnInput fn={handleChange} name={"cfName"} value={cfName} placeholder={"name"} />
                        </div>

                        <div>
                            <OwLabel title={"type"} />
                            <OwSelectOption
                                fn={handleChange}
                                name={"cfType"}
                                valueProp={"value"}
                                titleProp={"title"}
                                options={cfTypeOptions}
                            />
                        </div>

                        <div>
                            <OwLabel title={"destination_property"} />
                            <OwSelectOption
                                isDisabled={cfDisplayOnly}
                                fn={handleChange}
                                name={"cfDestProp"}
                                valueProp={"value"}
                                titleProp={"title"}
                                options={
                                    cfType ? (cfType === QbdCustomFieldsTypeEnum.ITEMS ? cfItemDestPropOptions : cfCustomerDestOptions) : []
                                }
                            />
                        </div>

                        <div>
                            <OwCheckbox
                                label={"display_only"}
                                fn={handleChange}
                                name={"cfDisplayOnly"}
                                checked={cfDisplayOnly}
                                truthyVal={true}
                                falsyVal={false}
                            />
                        </div>

                        <div>
                            <OwButton title={"save"} fn={saveFn} disableOnActive={true} active={saveActive} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-4 d-flex flex-column">
                <OwTitle title={"existing_custom_fields"} tag={"h5"} />
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th>
                                <OwLabel title={"name"} />
                            </th>
                            <th>
                                <OwLabel title={"type"} />
                            </th>
                            <th>
                                <OwLabel title={"destination_property"} />
                            </th>
                            <th>
                                <OwLabel title={"display_only"} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {existingCustomFields.map((cf, index) => {
                            return (
                                <QbdCustomFieldRow
                                    field={cf}
                                    updateFn={updateFn}
                                    deleteFn={deleteFn}
                                    key={cf.id}
                                    cfCustomerDestOptions={cfCustomerDestOptions}
                                    cfItemDestPropOptions={cfItemDestPropOptions}
                                    fieldTypeOptions={cfTypeOptions}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
